/**
 * Mỗi điểm cuối của API là MỘT hàm ở đây — màn hình không tự ghép đường dẫn hay tự gọi
 * `fetch`. Kiểu request/response lấy từ `@halo/shared`, cùng nguồn với backend.
 */
import {
  SHARED_AI_KEY_ID,
  thumbAssetPath,
  type AiAnalysisAccepted,
  type AiAnalysisStatus,
  type AiChannelSuggestRequest,
  type AiChannelSuggestionStatus,
  type AiPlaylistSuggestRequest,
  type AiPlaylistSuggestionStatus,
  type AiVideoSeoRequest,
  type AiVideoSeoStatus,
  type AiKeyDeletedResponse,
  type AiKeySavedResponse,
  type AiKeysResponse,
  type AiScreen,
  type OfficeAnalyzeResponse,
  type OfficeResearchResponse,
  type OfficeWeek,
  type YoutubeChannelBrandingDto,
  type YoutubeChannelBrandingSaveRequest,
  type ThumbAssetDto,
  type ThumbCompetitorVideosResponse,
  type ThumbDesignDto,
  type ThumbDesignSaveRequest,
  type ThumbDesignsResponse,
  type LiveStreamKeyDto,
  type YoutubeLiveMutationResponse,
  type YoutubeLiveResponse,
  type YoutubeLiveSaveRequest,
  type YoutubeMetadataRequest,
  type YoutubePublishChannelInfoRequest,
  type YoutubeVideoEditRequest,
  type YoutubePlaylistCreateRequest,
  type CrossPromoAction,
  type YoutubeDriveFolderRequest,
  type QuickTemplateDto,
  type QuickTemplateId,
  type QuickTemplateSettings,
  type QuickTemplatesResponse,
  type YoutubePlaylistRankingResponse,
  type YoutubePlaylistSortResponse,
  type YoutubePlaylistAddResponse,
  type YoutubeDriveFolderResponse,
  type YoutubeCrossPromoResponse,
  type YoutubeCrossPromoRunRequest,
  type YoutubeCrossPromoRunResponse,
  type YoutubePlaylistDto,
  type YoutubePlaylistMembershipResponse,
  type YoutubePlaylistsResponse,
  type YoutubePublishClientSaveRequest,
  type YoutubePublishConnectResponse,
  type YoutubePodcastCreateRequest,
  type YoutubePublishRequest,
  type YoutubePublishResponse,
  type YoutubePublishTemplateDto,
  type YoutubePublishTemplateSaveRequest,
  type YoutubePublishVideosResponse,
  type YoutubePublishWatchDto,
  type YoutubeUploadFinishRequest,
  type YoutubeUploadFinishResponse,
  type YoutubeUploadStartRequest,
  type YoutubeVideosDeleteRequest,
  type YoutubeUploadStartResponse,
} from '@halo/shared';
import type {
  GpmAgentCreatedResponse,
  GpmCommandDto,
  GpmCommandRequest,
  GpmStateResponse,
  AttendanceKind,
  AttendanceMarkRequest,
  AttendanceMarkResponse,
  AttendanceNetworkStatus,
  AttendanceNoteRequest,
  AttendanceNoteResponse,
  AttendanceRangeResponse,
  AttendanceRejectsResponse,
  AttendanceTodayResponse,
  CreateRequestInput,
  DecideRequestInput,
  RequestBox,
  RequestListResponse,
  RequestResponse,
  ActivityRange,
  ApproveStaffRequest,
  ApproveUserRequest,
  BoleroKeyCreateRequest,
  BoleroKeyCreatedResponse,
  BoleroKeyDeletedResponse,
  BoleroKeysResponse,
  BootstrapResponse,
  BulkChannelTransferResponse,
  ChangePasswordRequest,
  ChannelListResponse,
  ChannelMetricsResponse,
  ChannelOverviewResponse,
  ChannelRevenueOperation,
  ChannelRevenueResponse,
  ChannelRevenueSaveResponse,
  ChannelTransferResponse,
  CompetitorDetailResponse,
  CompetitorGroupCode,
  CompetitorListResponse,
  CompetitorRefreshDueResponse,
  CompetitorWeeklyViewsResponse,
  CreateOrgUnitRequest,
  CreateUserRequest,
  DeleteUserResponse,
  EmployeeProfileListResponse,
  GenerateTodayResponse,
  LoginHistoryResponse,
  LoginRequest,
  MeResponse,
  MetricRange,
  MoveStaffResponse,
  MusicFolderDto,
  MusicFolderInput,
  MusicLibraryResponse,
  MusicTrackDto,
  OrgChartResponse,
  OrgUnitResponse,
  ProjectFileDto,
  ProjectFileListResponse,
  PushConfigResponse,
  PushSavedResponse,
  PushSendResponse,
  PushStatusResponse,
  PushSubscriptionRequest,
  PushUnsubscribeRequest,
  RecordBatchResponse,
  RecordDisposition,
  RecordOperation,
  RegisterRequest,
  ResearchHistoryResponse,
  ResearchPermissionsResponse,
  ResearchPermissionsSavedResponse,
  RevenueReportPeriod,
  RevenueReportResponse,
  RevenueSummaryResponse,
  SaveEmployeeProfileRequest,
  SaveEmployeeProfileResponse,
  ScreenUsageResponse,
  MusicLiveHistoryResponse,
  MusicLiveSendRequest,
  MusicLiveSendResponse,
  MusicLiveSettingsDto,
  MusicLiveSettingsRequest,
  DownloadChannelInfoResponse,
  DownloadChannelVideosRequest,
  DownloadChannelVideosResponse,
  DownloadCreateRequest,
  DownloadCreateResponse,
  DownloadInfoResponse,
  DownloadInspectResponse,
  DownloadJobDto,
  DownloadStateResponse,
  ThumbnailToolRequest,
  ThumbnailToolResponse,
  UpdateUserRequest,
  UserResponse,
  YoutubeKeyDeletedResponse,
  YoutubeKeySavedResponse,
  YoutubeKeyUsageResponse,
  YoutubeKeysResponse,
  YoutubeOauthStatusResponse,
} from '@halo/shared';
import { apiRequest, apiUrl, uploadFile } from './client';

/** QUẢN LÝ GPM (11/10/2026): máy có cầu nối, ảnh chụp profile, hàng đợi lệnh. */
export const gpmApi = {
  state: () => apiRequest<GpmStateResponse>('/gpm'),
  createAgent: (name: string) =>
    apiRequest<GpmAgentCreatedResponse>('/gpm/agents', { body: { name } }),
  deleteAgent: (id: number) => apiRequest<void>(`/gpm/agents/${id}`, { method: 'DELETE' }),
  rotateAgent: (id: number) =>
    apiRequest<GpmAgentCreatedResponse>(`/gpm/agents/${id}/rotate`, { body: {} }),
  command: (body: GpmCommandRequest) => apiRequest<GpmCommandDto>('/gpm/commands', { body }),
  commandStatus: (id: number) => apiRequest<GpmCommandDto>(`/gpm/commands/${id}`),
};

export const authApi = {
  me: (signal?: AbortSignal) => apiRequest<MeResponse>('/auth/me', { signal }),
  login: (body: LoginRequest) => apiRequest<MeResponse>('/auth/login', { body }),
  logout: () => apiRequest<void>('/auth/logout', { method: 'POST' }),
  register: (body: RegisterRequest) =>
    apiRequest<{ status: 'PENDING' }>('/auth/register', { body }),
  changePassword: (body: ChangePasswordRequest) => apiRequest<void>('/auth/password', { body }),
};

export const bootstrapApi = {
  load: (signal?: AbortSignal) => apiRequest<BootstrapResponse>('/bootstrap', { signal }),
};

export const revenueApi = {
  byDate: (date: string, signal?: AbortSignal) =>
    apiRequest<ChannelRevenueResponse>('/channel-revenue', { query: { date }, signal }),
  byMonth: (month: string, signal?: AbortSignal) =>
    apiRequest<ChannelRevenueResponse>('/channel-revenue', { query: { month }, signal }),
  save: (operations: ChannelRevenueOperation[]) =>
    apiRequest<ChannelRevenueSaveResponse>('/channel-revenue', {
      method: 'PUT',
      body: { operations },
    }),
  summary: (month: string, signal?: AbortSignal) =>
    apiRequest<RevenueSummaryResponse>('/channel-revenue/summary', { query: { month }, signal }),
  report: (period: RevenueReportPeriod, anchor: string, signal?: AbortSignal) =>
    apiRequest<RevenueReportResponse>('/channel-revenue/report', {
      query: { period, anchor },
      signal,
    }),
  channels: (month?: string, signal?: AbortSignal) =>
    apiRequest<ChannelListResponse>('/channels', { query: { month }, signal }),
  /** Bàn giao kênh: máy chủ chuyển kênh + toàn bộ dữ liệu của kênh sang người mới trong một lần. */
  transferChannel: (code: string, to: string) =>
    apiRequest<ChannelTransferResponse>(`/channels/${encodeURIComponent(code)}/transfer`, {
      method: 'POST',
      body: { to },
    }),
  /** Bàn giao CẢ CỤM kênh của một người. `codes` bỏ trống = mọi kênh của họ trong phạm vi. */
  transferChannelsOf: (from: string, to: string, codes?: string[]) =>
    apiRequest<BulkChannelTransferResponse>('/channels/transfer-bulk', {
      method: 'POST',
      body: { from, to, codes },
    }),
};

const musicFolderPath = (id: string) => `/music/folders/${encodeURIComponent(id)}`;
const musicTrackPath = (id: string) => `/music/tracks/${encodeURIComponent(id)}`;

export const musicApi = {
  library: (signal?: AbortSignal) => apiRequest<MusicLibraryResponse>('/music/folders', { signal }),
  createFolder: (body: MusicFolderInput) => apiRequest<MusicFolderDto>('/music/folders', { body }),
  updateFolder: (id: string, body: MusicFolderInput) =>
    apiRequest<MusicFolderDto>(musicFolderPath(id), { method: 'PUT', body }),
  deleteFolder: (id: string) => apiRequest<void>(musicFolderPath(id), { method: 'DELETE' }),
  deleteTrack: (id: string) => apiRequest<void>(musicTrackPath(id), { method: 'DELETE' }),
  /** Đổi tên hiển thị; máy chủ giữ nguyên đuôi file và trả về tên cuối cùng. */
  renameTrack: (id: string, name: string) =>
    apiRequest<MusicTrackDto>(musicTrackPath(id), { method: 'PUT', body: { name } }),
  uploadTrack: (
    folderId: string,
    file: File,
    onProgress: (fraction: number) => void,
    signal: AbortSignal,
  ) =>
    uploadFile<MusicTrackDto>(`${musicFolderPath(folderId)}/tracks`, {
      query: { name: file.name },
      file,
      onProgress,
      signal,
    }),
  /** Cho thẻ `<audio>`: máy chủ kiểm quyền từng lần và hỗ trợ Range nên tua được giữa bài. */
  streamUrl: (trackId: string) => apiUrl(`${musicTrackPath(trackId)}/download`, { inline: 1 }),
  /** Cho thẻ `<a download>`: trình duyệt tự gửi cookie và tự hiện tiến độ tải. */
  downloadUrl: (trackId: string) => apiUrl(`${musicTrackPath(trackId)}/download`),
};

/**
 * TỆP ĐÍNH KÈM CỦA DỰ ÁN — file gốc của thumbnail (PSD, AI, file nén…). Ảnh xem trước vẫn đi đường
 * bản ghi ảnh; đây là chỗ cho file KHÔNG mở ra xem được trên web và nặng hàng chục MB.
 */
export const projectFilesApi = {
  list: (signal?: AbortSignal) => apiRequest<ProjectFileListResponse>('/project-files', { signal }),
  upload: (
    projectId: number,
    dateKey: string,
    folderId: number,
    file: File,
    onProgress: (fraction: number) => void,
    signal: AbortSignal,
  ) =>
    uploadFile<ProjectFileDto>(`/projects/${projectId}/files`, {
      query: { name: file.name, d: dateKey, fd: folderId },
      file,
      onProgress,
      signal,
    }),
  remove: (fileId: string) =>
    apiRequest<void>(`/project-files/${encodeURIComponent(fileId)}`, { method: 'DELETE' }),
  /** Cho thẻ `<a download>`: trình duyệt tự gửi cookie và tự hiện tiến độ tải. */
  downloadUrl: (fileId: string) => apiUrl(`/project-files/${encodeURIComponent(fileId)}/download`),
};

const userPath = (username: string) => `/users/${encodeURIComponent(username)}`;

/** Quản lý tài khoản — chỉ Giám đốc (máy chủ chặn ở một cửa duy nhất). */
export const usersApi = {
  create: (body: CreateUserRequest) => apiRequest<UserResponse>('/users', { body }),
  /** Chỉ gửi trường muốn đổi: tên hiển thị · chức vụ/phòng/nhóm · phòng kiêm (hai thứ sau: người đó bị đăng xuất) · bật/tắt. */
  update: (username: string, body: UpdateUserRequest) =>
    apiRequest<UserResponse>(userPath(username), { method: 'PATCH', body }),
  approve: (username: string, body: ApproveUserRequest) =>
    apiRequest<UserResponse>(`${userPath(username)}/approve`, { body }),
  reject: (username: string) =>
    apiRequest<void>(`${userPath(username)}/reject`, { method: 'POST' }),
  resetPassword: (username: string, password: string) =>
    apiRequest<void>(`${userPath(username)}/reset-password`, { body: { password } }),
  /** `records` vắng mặt chỉ hợp lệ khi tài khoản chưa nhập gì, hoặc là đơn chưa duyệt / bị từ chối. */
  remove: (username: string, query: { records?: RecordDisposition; transferTo?: string } = {}) =>
    apiRequest<DeleteUserResponse>(userPath(username), { method: 'DELETE', query }),
};

/**
 * Phân tích AI đi HAI CHẶNG: máy chủ nhận việc rồi trả lời ngay (202 + mã việc), giao diện hỏi lại kết
 * quả theo nhịp. Một lời gọi chờ thẳng vài phút sẽ bị lớp bảo vệ trước tên miền cắt ở 90 giây — bài vẫn
 * viết xong, công ty vẫn bị tính tiền, chỉ là không giao được.
 */
export const aiApi = {
  start: (body: { screen: AiScreen; table: unknown; question: string }) =>
    apiRequest<AiAnalysisAccepted>('/ai/analyses', { body }),
  status: (id: string, signal?: AbortSignal) =>
    apiRequest<AiAnalysisStatus>(`/ai/analyses/${encodeURIComponent(id)}`, { signal }),
  /** Tối ưu kênh › «Viết bằng AI» (mô tả + từ khoá kênh) — cũng hai chặng như trên. */
  suggestChannel: (body: AiChannelSuggestRequest) =>
    apiRequest<AiAnalysisAccepted>('/ai/channel-branding', { body }),
  suggestionStatus: (id: string, signal?: AbortSignal) =>
    apiRequest<AiChannelSuggestionStatus>(`/ai/channel-branding/${encodeURIComponent(id)}`, {
      signal,
    }),
  /** Tải lên · Video · Phát trực tiếp › «Viết bằng AI» (tiêu đề · mô tả · tag), máy chủ thêm tiêu đề đối thủ. */
  suggestVideo: (body: AiVideoSeoRequest) =>
    apiRequest<AiAnalysisAccepted>('/ai/video-seo', { body }),
  videoSeoStatus: (id: string, signal?: AbortSignal) =>
    apiRequest<AiVideoSeoStatus>(`/ai/video-seo/${encodeURIComponent(id)}`, { signal }),
  /** Hộp sửa video › «Gợi ý danh sách phát bằng AI» (tên + mô tả; tạo thật qua `createPlaylist`). */
  suggestPlaylist: (body: AiPlaylistSuggestRequest) =>
    apiRequest<AiAnalysisAccepted>('/ai/playlist', { body }),
  playlistSuggestionStatus: (id: string, signal?: AbortSignal) =>
    apiRequest<AiPlaylistSuggestionStatus>(`/ai/playlist/${encodeURIComponent(id)}`, { signal }),
};

/** `''` (khoá chung) không đặt được lên đường dẫn — API dùng mã riêng cho nó. */
const aiKeyPath = (departmentId: string) =>
  `/ai/keys/${encodeURIComponent(departmentId || SHARED_AI_KEY_ID)}`;

/** Khoá API Phân tích AI — chỉ Giám đốc. Khoá đầy đủ chỉ đi MỘT chiều (lên máy chủ), không bao giờ về. */
export const aiKeysApi = {
  overview: (month: string | undefined, signal?: AbortSignal) =>
    apiRequest<AiKeysResponse>('/ai/keys', { query: { month }, signal }),
  /** Máy chủ THỬ GỌI Anthropic bằng khoá này trước khi lưu; khoá sai thì không lưu. */
  save: (departmentId: string, apiKey: string) =>
    apiRequest<AiKeySavedResponse>(aiKeyPath(departmentId), { method: 'PUT', body: { apiKey } }),
  remove: (departmentId: string) =>
    apiRequest<AiKeyDeletedResponse>(aiKeyPath(departmentId), { method: 'DELETE' }),
};

/**
 * Key kích hoạt phần mềm BOLERO RENDER — chỉ Giám đốc. Máy chủ ký key và ghi sổ; web chỉ hiện key để
 * sao chép gửi nhân viên (key không phải bí mật của máy chủ: nó chỉ mở đúng máy / đúng hạn ghi trong đó).
 */
export const boleroKeysApi = {
  list: (signal?: AbortSignal) => apiRequest<BoleroKeysResponse>('/bolero/keys', { signal }),
  create: (body: BoleroKeyCreateRequest) =>
    apiRequest<BoleroKeyCreatedResponse>('/bolero/keys', { body }),
  /** Xoá khỏi SỔ — không thu hồi được key trên máy đã kích hoạt (phần mềm kiểm ngoại tuyến). */
  remove: (id: number) =>
    apiRequest<BoleroKeyDeletedResponse>(`/bolero/keys/${id}`, { method: 'DELETE' }),
};

const orgUnitPath = (id: string) => `/org-units/${encodeURIComponent(id)}`;

/** Phòng ban & nhóm — chỉ Giám đốc. Mã đơn vị không bao giờ đổi; đổi tên chỉ đổi tên hiển thị. */
export const orgUnitsApi = {
  create: (body: CreateOrgUnitRequest) => apiRequest<OrgUnitResponse>('/org-units', { body }),
  rename: (id: string, name: string) =>
    apiRequest<OrgUnitResponse>(orgUnitPath(id), { method: 'PATCH', body: { name } }),
  /** Máy chủ chỉ xoá đơn vị RỖNG (không còn người, phòng thì không còn nhóm). */
  remove: (id: string) => apiRequest<void>(orgUnitPath(id), { method: 'DELETE' }),
};

/** Trưởng phòng (và Giám đốc) xếp lại NHÓM cho người trong phòng. */
const staffPath = (username: string) => `/staff/${encodeURIComponent(username)}`;

/** Việc Trưởng phòng làm được trong phòng mình (Giám đốc dùng `usersApi`, gán được mọi phòng). */
export const staffApi = {
  moveToTeam: (username: string, teamId: string) =>
    apiRequest<MoveStaffResponse>(`${staffPath(username)}/move`, { body: { teamId } }),
  /** Duyệt đơn vào phòng của người duyệt: chỉ chức vụ (Trưởng nhóm / Nhân viên) và nhóm. */
  approve: (username: string, body: ApproveStaffRequest) =>
    apiRequest<UserResponse>(`${staffPath(username)}/approve`, { body }),
  reject: (username: string) =>
    apiRequest<void>(`${staffPath(username)}/reject`, { method: 'POST' }),
};

/** Lịch sử truy cập: ai đăng nhập lúc nào, ai mở mục nào. */
export const activityApi = {
  /**
   * Báo «vừa mở màn hình này». Gọi rồi quên: hỏng mạng hay bị chặn thì thôi, không bao giờ làm
   * phiền người dùng vì một việc ghi nhận phụ.
   */
  trackScreen: (path: string) =>
    apiRequest<void>('/activity/screen', { body: { path } }).catch(() => undefined),
  logins: (range: ActivityRange, signal?: AbortSignal) =>
    apiRequest<LoginHistoryResponse>('/activity/logins', { query: { range }, signal }),
  usage: (range: ActivityRange, signal?: AbortSignal) =>
    apiRequest<ScreenUsageResponse>('/activity/usage', { query: { range }, signal }),
};

/** Nghiên cứu kênh đối thủ — máy chủ gọi YouTube Data API bằng khoá của công ty. */
export const competitorsApi = {
  list: (signal?: AbortSignal) => apiRequest<CompetitorListResponse>('/competitors', { signal }),
  detail: (id: number, signal?: AbortSignal) =>
    apiRequest<CompetitorDetailResponse>(`/competitors/${id}`, { signal }),
  /** Dán link → phân tích. Kênh đã có trong danh sách thì máy chủ cập nhật lại kênh đó. */
  add: (url: string, maxVideos: number) =>
    apiRequest<CompetitorDetailResponse>('/competitors', { body: { url, maxVideos } }),
  refresh: (id: number, maxVideos?: number) =>
    apiRequest<CompetitorDetailResponse>(`/competitors/${id}/refresh`, {
      body: maxVideos ? { maxVideos } : {},
    }),
  remove: (id: number) => apiRequest<void>(`/competitors/${id}`, { method: 'DELETE' }),
  /** Xếp kênh vào nhóm: đối thủ trực tiếp · gián tiếp · tìm kiếm thị trường. */
  setGroup: (id: number, group: CompetitorGroupCode) =>
    apiRequest<{ ok: true }>(`/competitors/${id}/group`, { method: 'PUT', body: { group } }),
  /**
   * View tuần của các kênh mình đang theo dõi, suy từ mốc đã lưu — cho màn «Thị trường». KHÔNG gọi
   * YouTube nên không tốn hạn mức, bấm bao nhiêu lần cũng được.
   */
  weeklyViews: (weeks: { key: string; weekStart: string }[]) =>
    apiRequest<CompetitorWeeklyViewsResponse>('/competitors/weekly-views', { body: { weeks } }),
  /** Gọi khi mở mục: máy chủ cập nhật các kênh đã quá 20 giờ. Gọi thừa vô hại. */
  refreshDue: () =>
    apiRequest<CompetitorRefreshDueResponse>('/competitors/refresh-due', { method: 'POST' }),
  /** Lịch sử ai dùng mục này — chỉ Giám đốc gọi được (máy chủ chặn người khác). */
  history: (signal?: AbortSignal) =>
    apiRequest<ResearchHistoryResponse>('/competitors/history', { signal }),
  permissions: (signal?: AbortSignal) =>
    apiRequest<ResearchPermissionsResponse>('/competitors/permissions', { signal }),
  savePermissions: (allowed: string[]) =>
    apiRequest<ResearchPermissionsSavedResponse>('/competitors/permissions', {
      method: 'PUT',
      body: { allowed },
    }),
};

/** Kho khoá YouTube Data API — chỉ Giám đốc. Khoá đi một chiều lên máy chủ. */
export const youtubeKeysApi = {
  list: (signal?: AbortSignal) => apiRequest<YoutubeKeysResponse>('/youtube-keys', { signal }),
  add: (label: string, apiKey: string) =>
    apiRequest<YoutubeKeySavedResponse>('/youtube-keys', { body: { label, apiKey } }),
  retry: (id: number) =>
    apiRequest<YoutubeKeySavedResponse>(`/youtube-keys/${id}/retry`, { method: 'POST' }),
  remove: (id: number) =>
    apiRequest<YoutubeKeyDeletedResponse>(`/youtube-keys/${id}`, { method: 'DELETE' }),
  /** Sổ «khoá dùng vào đâu» của ngày hạn mức hiện tại (17/10/2026). */
  usage: (signal?: AbortSignal) =>
    apiRequest<YoutubeKeyUsageResponse>('/youtube-keys/usage', { signal }),
};

export const orgChartApi = {
  get: (signal?: AbortSignal) => apiRequest<OrgChartResponse>('/org-chart', { signal }),
};

export const profilesApi = {
  list: (signal?: AbortSignal) =>
    apiRequest<EmployeeProfileListResponse>('/employee-profiles', { signal }),
  /** Chỉ gửi những ô muốn đổi — ô vắng mặt thì máy chủ giữ nguyên giá trị đang lưu. */
  save: (username: string, body: SaveEmployeeProfileRequest) =>
    apiRequest<SaveEmployeeProfileResponse>(`/employee-profiles/${encodeURIComponent(username)}`, {
      method: 'PATCH',
      body,
    }),
};

export const schedulesApi = {
  generateToday: () =>
    apiRequest<GenerateTodayResponse>('/schedules/generate-today', { method: 'POST' }),
};

const publishPath = (youtubeId: string, rest = '') =>
  `/youtube-publish/${encodeURIComponent(youtubeId)}${rest}`;

/** Ảnh (JPG/PNG) gửi nguyên byte, máy chủ chuyển tiếp cho YouTube. */
const imageBody = (file: Blob) => ({
  method: 'POST' as const,
  rawBody: file,
  headers: { 'Content-Type': file.type || 'image/jpeg' },
});

/**
 * Màn «Đăng video» (06/10/2026) — chỉ Giám đốc: tải video lên, hẹn giờ công khai, mẫu tiêu đề · mô tả · tag,
 * danh sách phát, tối ưu kênh.
 */
export const youtubePublishApi = {
  videos: (youtubeId: string, limit?: number) =>
    apiRequest<YoutubePublishVideosResponse>(publishPath(youtubeId, '/videos'), {
      query: { limit },
    }),
  apply: (youtubeId: string, body: YoutubePublishRequest) =>
    apiRequest<YoutubePublishResponse>(publishPath(youtubeId), { body }),
  metadata: (youtubeId: string, body: YoutubeMetadataRequest) =>
    apiRequest<YoutubePublishResponse>(publishPath(youtubeId, '/metadata'), { body }),
  /** Sửa MỘT video trọn gói: nội dung + hiển thị + danh sách phát (07/10/2026). */
  editVideo: (youtubeId: string, videoId: string, body: YoutubeVideoEditRequest) =>
    apiRequest<YoutubePublishResponse>(
      publishPath(youtubeId, `/videos/${encodeURIComponent(videoId)}`),
      { method: 'PUT', body },
    ),
  template: (youtubeId: string) =>
    apiRequest<YoutubePublishTemplateDto>(publishPath(youtubeId, '/template')),
  saveTemplate: (youtubeId: string, body: YoutubePublishTemplateSaveRequest) =>
    apiRequest<YoutubePublishTemplateDto>(publishPath(youtubeId, '/template'), {
      method: 'PUT',
      body,
    }),
  playlists: (youtubeId: string) =>
    apiRequest<YoutubePlaylistsResponse>(publishPath(youtubeId, '/playlists')),
  /** Video nằm ở danh sách phát nào — cột «Danh sách phát» ở phần Video (1 đơn vị mỗi trang 50). */
  playlistMembership: (youtubeId: string) =>
    apiRequest<YoutubePlaylistMembershipResponse>(publishPath(youtubeId, '/playlist-items')),
  /** Link thư mục Google Drive (09/10/2026): seo.txt + ảnh bìa cho Phát trực tiếp. */
  driveFolder: (body: YoutubeDriveFolderRequest) =>
    apiRequest<YoutubeDriveFolderResponse>('/youtube-publish/drive-folder', { body }),
  /** LIÊN KẾT KÊNH (09/10/2026): trạng thái của mọi kênh; chạy một mục cho một kênh. */
  crossPromo: () => apiRequest<YoutubeCrossPromoResponse>('/youtube-publish/crosspromo'),
  runCrossPromo: (youtubeId: string, action: CrossPromoAction, body: YoutubeCrossPromoRunRequest) =>
    apiRequest<YoutubeCrossPromoRunResponse>(publishPath(youtubeId, `/crosspromo/${action}`), {
      body,
    }),
  /** Xếp danh sách phát theo view 60 phút (09/10/2026): xem trước, rồi dời trên YouTube. */
  playlistRanking: (youtubeId: string, playlistId: string) =>
    apiRequest<YoutubePlaylistRankingResponse>(
      publishPath(youtubeId, `/playlists/${encodeURIComponent(playlistId)}/ranking`),
    ),
  sortPlaylistViews60: (youtubeId: string, playlistId: string) =>
    apiRequest<YoutubePlaylistSortResponse>(
      publishPath(youtubeId, `/playlists/${encodeURIComponent(playlistId)}/sort-views60`),
      { body: {} },
    ),
  /** Xếp theo thứ tự tự chọn / kéo thả (10/10/2026): toàn bộ mã mục theo thứ tự mới. */
  reorderPlaylist: (youtubeId: string, playlistId: string, itemIds: string[]) =>
    apiRequest<YoutubePlaylistSortResponse>(
      publishPath(youtubeId, `/playlists/${encodeURIComponent(playlistId)}/reorder`),
      { body: { itemIds } },
    ),
  /** Thêm các video đã tích vào danh sách phát (50 đơn vị / video); trả thứ tự mới. */
  addPlaylistVideos: (youtubeId: string, playlistId: string, videoIds: string[]) =>
    apiRequest<YoutubePlaylistAddResponse>(
      publishPath(youtubeId, `/playlists/${encodeURIComponent(playlistId)}/items`),
      { body: { videoIds } },
    ),
  /** Bỏ một video khỏi danh sách phát (50 đơn vị); trả thứ tự mới. */
  removePlaylistItem: (youtubeId: string, playlistId: string, itemId: string) =>
    apiRequest<YoutubePlaylistRankingResponse>(
      publishPath(
        youtubeId,
        `/playlists/${encodeURIComponent(playlistId)}/items/${encodeURIComponent(itemId)}`,
      ),
      { method: 'DELETE' },
    ),
  /** Tạo danh sách phát mới trên kênh (50 đơn vị). */
  createPlaylist: (youtubeId: string, body: YoutubePlaylistCreateRequest) =>
    apiRequest<YoutubePlaylistDto>(publishPath(youtubeId, '/playlists'), { body }),
  /** PHÁT TRỰC TIẾP (06/10/2026): lịch + khoá luồng, lên lịch, sửa, xoá; ảnh bìa đi đường `thumbnail`. */
  live: (youtubeId: string) => apiRequest<YoutubeLiveResponse>(publishPath(youtubeId, '/live')),
  createLive: (youtubeId: string, body: YoutubeLiveSaveRequest) =>
    apiRequest<YoutubeLiveMutationResponse>(publishPath(youtubeId, '/live'), { body }),
  updateLive: (youtubeId: string, broadcastId: string, body: YoutubeLiveSaveRequest) =>
    apiRequest<YoutubeLiveMutationResponse>(
      publishPath(youtubeId, `/live/${encodeURIComponent(broadcastId)}`),
      { method: 'PUT', body },
    ),
  /** Mã luồng để dán vào OBS — đọc đúng lúc bấm, không lưu ở đâu. */
  streamKey: (youtubeId: string, streamId: string) =>
    apiRequest<LiveStreamKeyDto>(
      publishPath(youtubeId, `/live/streams/${encodeURIComponent(streamId)}/key`),
    ),
  /** Xoá video khỏi kênh — không hoàn tác được. */
  deleteVideos: (youtubeId: string, body: YoutubeVideosDeleteRequest) =>
    apiRequest<YoutubePublishResponse>(publishPath(youtubeId, '/videos/delete'), { body }),
  deleteLive: (youtubeId: string, broadcastId: string) =>
    apiRequest<{ deleted: true }>(
      publishPath(youtubeId, `/live/${encodeURIComponent(broadcastId)}`),
      {
        method: 'DELETE',
      },
    ),
  /** Mở phiên tải lên; file thì trình duyệt tự PUT vào `uploadUrl` (xem `upload-runner.ts`). */
  startUpload: (youtubeId: string, body: YoutubeUploadStartRequest) =>
    apiRequest<YoutubeUploadStartResponse>(publishPath(youtubeId, '/uploads'), { body }),
  finishUpload: (youtubeId: string, body: YoutubeUploadFinishRequest) =>
    apiRequest<YoutubeUploadFinishResponse>(publishPath(youtubeId, '/uploads/finish'), { body }),
  /** Tạo podcast (08/10/2026): thông tin trên query, ảnh vuông là thân. */
  createPodcast: (youtubeId: string, query: YoutubePodcastCreateRequest, file: Blob) =>
    apiRequest<YoutubePlaylistDto>(publishPath(youtubeId, '/podcasts'), {
      ...imageBody(file),
      query,
    }),
  thumbnail: (youtubeId: string, videoId: string, file: Blob) =>
    apiRequest<{ ok: true }>(
      publishPath(youtubeId, `/videos/${encodeURIComponent(videoId)}/thumbnail`),
      imageBody(file),
    ),
  channel: (youtubeId: string) =>
    apiRequest<YoutubeChannelBrandingDto>(publishPath(youtubeId, '/channel')),
  saveChannel: (youtubeId: string, body: YoutubeChannelBrandingSaveRequest) =>
    apiRequest<YoutubeChannelBrandingDto>(publishPath(youtubeId, '/channel'), {
      method: 'PUT',
      body,
    }),
  channelBanner: (youtubeId: string, file: Blob) =>
    apiRequest<YoutubeChannelBrandingDto>(
      publishPath(youtubeId, '/channel/banner'),
      imageBody(file),
    ),
  /** KÊNH CẦN LÀM HÔM NAY (11/10/2026): ảnh chụp lần kiểm tra gần nhất; `checkWatch` chạy kiểm tra ngay (~3 đơn vị/kênh). */
  watch: () => apiRequest<YoutubePublishWatchDto>('/youtube-publish/watch'),
  checkWatch: () =>
    apiRequest<YoutubePublishWatchDto>('/youtube-publish/watch/check', { body: {} }),
  /** Kết nối riêng của Đăng video: client Google + kênh đã nối (06/10/2026). */
  connect: () => apiRequest<YoutubePublishConnectResponse>('/youtube-publish/connect'),
  saveClient: (body: YoutubePublishClientSaveRequest) =>
    apiRequest<YoutubePublishConnectResponse>('/youtube-publish/connect/client', {
      method: 'PUT',
      body,
    }),
  /** Bỏ một client xoay vòng (08/10/2026). */
  deleteClient: (key: string) =>
    apiRequest<YoutubePublishConnectResponse>(
      `/youtube-publish/connect/clients/${encodeURIComponent(key)}`,
      { method: 'DELETE' },
    ),
  /** Chia đều kênh cho các dự án / giao dự án chính cho một kênh (08/10/2026). */
  balanceChannels: () =>
    apiRequest<YoutubePublishConnectResponse>('/youtube-publish/connect/balance', { body: {} }),
  setChannelClient: (youtubeId: string, clientKey: string) =>
    apiRequest<YoutubePublishConnectResponse>(
      `/youtube-publish/connect/channels/${encodeURIComponent(youtubeId)}/client`,
      { method: 'PUT', body: { clientKey } },
    ),
  /** Nối kênh bằng client đã chọn; bỏ trống = client đầu tiên. */
  startConnect: (clientKey?: string) =>
    apiRequest<{ url: string }>('/youtube-publish/connect/start', {
      body: clientKey ? { clientKey } : {},
    }),
  disconnect: (youtubeId: string) =>
    apiRequest<YoutubePublishConnectResponse>(
      `/youtube-publish/connect/channels/${encodeURIComponent(youtubeId)}`,
      { method: 'DELETE' },
    ),
  /** NHÓM KÊNH (06/10/2026): như thư mục cho kênh đã nối — mọi lệnh trả về trạng thái Kết nối mới. */
  createGroup: (body: { name: string }) =>
    apiRequest<YoutubePublishConnectResponse>('/youtube-publish/groups', { body }),
  renameGroup: (groupId: string, body: { name: string }) =>
    apiRequest<YoutubePublishConnectResponse>(
      `/youtube-publish/groups/${encodeURIComponent(groupId)}`,
      { method: 'PUT', body },
    ),
  deleteGroup: (groupId: string) =>
    apiRequest<YoutubePublishConnectResponse>(
      `/youtube-publish/groups/${encodeURIComponent(groupId)}`,
      { method: 'DELETE' },
    ),
  setChannelGroup: (youtubeId: string, groupId: string) =>
    apiRequest<YoutubePublishConnectResponse>(
      `/youtube-publish/connect/channels/${encodeURIComponent(youtubeId)}/group`,
      { method: 'PUT', body: { groupId } },
    ),
  /** Ba ô tự nhập của kênh đã nối: network · kiếm tiền · mail quản lý (07/10/2026). */
  setChannelInfo: (youtubeId: string, body: YoutubePublishChannelInfoRequest) =>
    apiRequest<YoutubePublishConnectResponse>(
      `/youtube-publish/connect/channels/${encodeURIComponent(youtubeId)}/info`,
      { method: 'PUT', body },
    ),
};

/** Phân tích kênh văn phòng (06/10/2026): kênh công ty đo bằng thước đo của mục đối thủ, gom theo bộ máy. */
export const officeResearchApi = {
  /** Số đã lưu — không gọi YouTube. */
  stats: (weeks: OfficeWeek[], signal?: AbortSignal) =>
    apiRequest<OfficeResearchResponse>('/office-research/stats', { body: { weeks }, signal }),
  analyze: (codes: string[], weeks: OfficeWeek[]) =>
    apiRequest<OfficeAnalyzeResponse>('/office-research/analyze', { body: { codes, weeks } }),
  detail: (code: string, signal?: AbortSignal) =>
    apiRequest<CompetitorDetailResponse>(`/office-research/channels/${encodeURIComponent(code)}`, {
      signal,
    }),
};

export const youtubeOauthApi = {
  status: () => apiRequest<YoutubeOauthStatusResponse>('/youtube-oauth'),
  saveClient: (body: { clientId: string; clientSecret: string }) =>
    apiRequest<YoutubeOauthStatusResponse>('/youtube-oauth/client', { method: 'PUT', body }),
  start: () => apiRequest<{ url: string }>('/youtube-oauth/start', { body: {} }),
  refreshMetrics: ({ youtubeId, range }: { youtubeId: string; range: MetricRange }) =>
    apiRequest<ChannelMetricsResponse>(
      `/youtube-oauth/channels/${encodeURIComponent(youtubeId)}/refresh?range=${range}`,
      { body: {} },
    ),
  disconnect: (youtubeId: string) =>
    apiRequest<YoutubeOauthStatusResponse>(
      `/youtube-oauth/channels/${encodeURIComponent(youtubeId)}`,
      { method: 'DELETE' },
    ),
};

/**
 * Tải thumbnail đối thủ — máy chủ chỉ liệt kê video của kênh (YouTube Data API); ảnh thì trình duyệt
 * tự lấy thẳng từ máy chủ ảnh của YouTube.
 */
/**
 * Làm thumbnail (06/10/2026): bản thiết kế + tài sản ảnh ở máy chủ; vẽ và xuất JPG ở trình duyệt. Ảnh tải lên
 * bằng byte thô (`uploadFile`), đọc về bằng địa chỉ `assetUrl` (cùng gốc, canvas không bị nhiễm).
 */
export const thumbnailMakerApi = {
  /** Mẫu «Thumb video ngắn» đã chỉnh (10/10/2026): đọc · lưu đè · về mẫu gốc. */
  quickTemplates: () => apiRequest<QuickTemplatesResponse>('/thumbnail-maker/quick-templates'),
  saveQuickTemplate: (id: QuickTemplateId, settings: QuickTemplateSettings) =>
    apiRequest<QuickTemplateDto>(`/thumbnail-maker/quick-templates/${id}`, {
      method: 'PUT',
      body: settings,
    }),
  resetQuickTemplate: (id: QuickTemplateId) =>
    apiRequest<{ reset: true }>(`/thumbnail-maker/quick-templates/${id}`, { method: 'DELETE' }),
  designs: () => apiRequest<ThumbDesignsResponse>('/thumbnail-maker/designs'),
  design: (id: string) =>
    apiRequest<ThumbDesignDto>(`/thumbnail-maker/designs/${encodeURIComponent(id)}`),
  create: (body: ThumbDesignSaveRequest) =>
    apiRequest<ThumbDesignDto>('/thumbnail-maker/designs', { body }),
  update: (id: string, body: ThumbDesignSaveRequest) =>
    apiRequest<ThumbDesignDto>(`/thumbnail-maker/designs/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body,
    }),
  remove: (id: string) =>
    apiRequest<{ deleted: true }>(`/thumbnail-maker/designs/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  /** Chuyển bản vào / ra thư mục «Mẫu đã chỉnh sửa». */
  setTemplate: (id: string, isTemplate: boolean) =>
    apiRequest<ThumbDesignDto>(`/thumbnail-maker/designs/${encodeURIComponent(id)}/template`, {
      method: 'PUT',
      body: { isTemplate },
    }),
  uploadAsset: (file: Blob, onProgress?: (fraction: number) => void) =>
    uploadFile<ThumbAssetDto>('/thumbnail-maker/assets', { file, onProgress }),
  assetUrl: (id: string) => apiUrl(thumbAssetPath(id)),
  competitorVideos: (q: string, signal?: AbortSignal) =>
    apiRequest<ThumbCompetitorVideosResponse>('/thumbnail-maker/competitor-videos', {
      query: { q },
      signal,
    }),
};

/** Tab «Music Live» của Đăng video (18/10/2026): chèn video sang Music Live của BH Media qua máy chủ. */
export const musicLiveApi = {
  settings: (signal?: AbortSignal) =>
    apiRequest<MusicLiveSettingsDto>('/music-live/settings', { signal }),
  saveSettings: (request: MusicLiveSettingsRequest) =>
    apiRequest<MusicLiveSettingsDto>('/music-live/settings', { method: 'PUT', body: request }),
  history: (signal?: AbortSignal) =>
    apiRequest<MusicLiveHistoryResponse>('/music-live/history', { signal }),
  send: (request: MusicLiveSendRequest) =>
    apiRequest<MusicLiveSendResponse>('/music-live/send', { body: request }),
};

/** Tải video (28/10/2026): dán link kênh / link video → máy công ty tải file gốc về thư mục. */
export const videoDownloadApi = {
  state: (signal?: AbortSignal) => apiRequest<DownloadStateResponse>('/video-download', { signal }),
  inspect: (links: string[]) =>
    apiRequest<DownloadInspectResponse>('/video-download/inspect', { body: { links } }),
  /** Tiêu đề · mô tả · tag của video + tag kênh — hỏi YouTube bằng kho khoá công ty (2 đơn vị). */
  info: (videoId: string) =>
    apiRequest<DownloadInfoResponse>('/video-download/info', { body: { videoId } }),
  /** Danh sách video của một kênh công ty đã nối ở Đăng video — chỉ Giám đốc. */
  channelVideos: (body: DownloadChannelVideosRequest) =>
    apiRequest<DownloadChannelVideosResponse>('/video-download/channel-videos', { body }),
  /** Thông tin kênh theo link — kênh bất kỳ công khai: tên · @handle · mô tả · tag kênh · số liệu (1 đơn vị). */
  channelInfo: (link: string) =>
    apiRequest<DownloadChannelInfoResponse>('/video-download/channel-info', { body: { link } }),
  create: (body: DownloadCreateRequest) =>
    apiRequest<DownloadCreateResponse>('/video-download/jobs', { body }),
  cancel: (jobId: number) =>
    apiRequest<DownloadJobDto>(`/video-download/jobs/${jobId}/cancel`, { method: 'POST' }),
  retry: (jobId: number) =>
    apiRequest<DownloadJobDto>(`/video-download/jobs/${jobId}/retry`, { method: 'POST' }),
  remove: (jobId: number) =>
    apiRequest<void>(`/video-download/jobs/${jobId}`, { method: 'DELETE' }),
};

export const thumbnailToolApi = {
  /** Lượt đầu gửi `url`; các lượt sau gửi `playlistId` + `pageToken` máy chủ đã trả. */
  videos: (request: ThumbnailToolRequest, signal?: AbortSignal) =>
    apiRequest<ThumbnailToolResponse>('/thumbnail-tool/videos', { body: request, signal }),
};

/** Thông báo đẩy về thiết bị — bật / tắt trên máy đang mở, gửi thử, và tình hình ai đã bật. */
export const pushApi = {
  /** Khoá công khai của máy chủ — trình duyệt cần nó để đăng ký nhận thông báo. */
  config: () => apiRequest<PushConfigResponse>('/push/config'),
  subscribe: (subscription: PushSubscriptionRequest) =>
    apiRequest<PushSavedResponse>('/push/subscription', { method: 'PUT', body: subscription }),
  unsubscribe: (request: PushUnsubscribeRequest) =>
    apiRequest<PushSavedResponse>('/push/unsubscribe', { body: request }),
  /** Gửi một thông báo thử tới các máy của chính người gọi. */
  test: () => apiRequest<PushSendResponse>('/push/test', { method: 'POST' }),
  /** Ai đã bật thông báo — chỉ Giám đốc gọi được (máy chủ chặn người khác). */
  status: (signal?: AbortSignal) => apiRequest<PushStatusResponse>('/push/status', { signal }),
};

/** ĐƠN TỪ VÀ PHIẾU ĐỀ XUẤT (05/10/2026) — chín mẫu, duyệt theo cấp, hành chính / kế toán thực hiện. */
export const requestsApi = {
  /** `mine` phiếu của tôi · `all` mọi phiếu tôi được thấy (phạm vi, chờ tôi duyệt, tôi thực hiện). */
  list: (box: RequestBox, signal?: AbortSignal) =>
    apiRequest<RequestListResponse>('/requests', { query: { box }, signal }),
  create: (input: CreateRequestInput) =>
    apiRequest<{ id: number; code: string }>('/requests', { method: 'POST', body: input }),
  decide: (id: number, input: DecideRequestInput) =>
    apiRequest<RequestResponse>(`/requests/${id}/decision`, { method: 'POST', body: input }),
  /** Hành chính / kế toán đánh dấu đã thực hiện (đã mua, đã chi…), kèm ghi chú. */
  execute: (id: number, note: string) =>
    apiRequest<RequestResponse>(`/requests/${id}/execute`, { method: 'POST', body: { note } }),
  /** Người lập rút phiếu còn chờ; Giám đốc huỷ phiếu chờ / đã duyệt. */
  cancel: (id: number, reason = '') =>
    apiRequest<RequestResponse>(`/requests/${id}/cancel`, { method: 'POST', body: { reason } }),
  /** Giám đốc xoá hẳn phiếu lập sai, lập thử. */
  remove: (id: number) => apiRequest<{ ok: true }>(`/requests/${id}`, { method: 'DELETE' }),
};

/** CHẤM CÔNG (05/10/2026) — giờ chấm do máy chủ ghi, trình duyệt không gửi giờ nào. */
export const attendanceApi = {
  /** Hôm nay của chính người gọi: các lần đã chấm, dấu tay, giờ máy chủ. */
  today: (signal?: AbortSignal) =>
    apiRequest<AttendanceTodayResponse>('/attendance/today', { signal }),
  /**
   * Bấm «Vào ca» / «Ra ca» (`kind`, bản bàn giao 07/10/2026); `reason` chỉ gửi khi máy chủ đòi lý do đi trễ.
   * `fromApp`: trang đang chạy như app mở từ màn hình chính — cho luật «chỉ chấm trên app điện thoại».
   */
  punch: ({
    kind,
    reason,
    fromApp,
  }: {
    kind: AttendanceKind;
    reason?: string | undefined;
    fromApp: boolean;
  }) =>
    apiRequest<AttendanceTodayResponse>('/attendance/punch', {
      method: 'POST',
      body: reason ? { kind, reason, fromApp } : { kind, fromApp },
    }),
  /** Mạng của máy đang dùng, theo máy chủ thấy. */
  network: (signal?: AbortSignal) =>
    apiRequest<AttendanceNetworkStatus>('/attendance/network', { signal }),
  /** Lần chấm bị từ chối vì ngoài mạng công ty, 7 ngày gần nhất — chỉ Giám đốc. */
  rejects: (signal?: AbortSignal) =>
    apiRequest<AttendanceRejectsResponse>('/attendance/rejects', { signal }),
  /** Chấm công trong phạm vi người gọi: một ngày (`date`), khoảng ngày (`from`–`to`, tối đa 92 ngày) hoặc cả tháng (`month`). */
  range: (
    query: { month?: string; date?: string; from?: string; to?: string },
    signal?: AbortSignal,
  ) => apiRequest<AttendanceRangeResponse>('/attendance', { query, signal }),
  /** Quản lý đặt / bỏ dấu tay cho một người một ngày. */
  mark: (request: AttendanceMarkRequest) =>
    apiRequest<AttendanceMarkResponse>('/attendance/mark', { method: 'PUT', body: request }),
  /** Quản lý ghi / xoá (`note` rỗng) ghi chú cho một người một ngày — cột «Ghi chú». */
  note: (request: AttendanceNoteRequest) =>
    apiRequest<AttendanceNoteResponse>('/attendance/note', { method: 'PUT', body: request }),
};

export const channelMetricsApi = {
  list: (range: MetricRange) =>
    apiRequest<ChannelMetricsResponse>(`/channel-metrics?range=${range}`),
  /** Thời gian thực (48 giờ / 60 phút) + doanh thu theo ngày của mọi kênh đã nối (07/10/2026). */
  overview: (signal?: AbortSignal) =>
    apiRequest<ChannelOverviewResponse>('/channel-metrics/overview', { signal }),
  /** Thống kê kênh Đăng video (08/10/2026): cùng dạng với «Thời gian thực», không có doanh thu. */
  publishStats: (signal?: AbortSignal) =>
    apiRequest<ChannelOverviewResponse>('/youtube-publish/stats', { signal }),
  refreshPublishStats: () =>
    apiRequest<ChannelOverviewResponse>('/youtube-publish/stats/refresh', { body: {} }),
  /** Nút «Lấy số mới»: máy chủ lấy mẫu và số liệu ngày của mọi kênh ngay rồi trả tổng quan mới. */
  refreshOverview: () =>
    apiRequest<ChannelOverviewResponse>('/channel-metrics/overview/refresh', { body: {} }),
};

export const recordsApi = {
  batch: (operations: RecordOperation[]) =>
    apiRequest<RecordBatchResponse>('/records/batch', { body: { operations } }),
};
